import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Column } from "../../../../core/components/Column";
import { CustomButton } from "../../../../core/components/CustomButton";
import CircularProgress from "../../../../core/components/CircularProgress";
import { TextComponent } from "../../../../core/components/TextComponent";
import {
	incomingLikeEmailPreferenceApi,
	LikeEmailFrequency,
} from "../../data/likeEmailPreferenceApi";
import { LikeEmailFrequencySelector } from "../components/LikeEmailFrequencySelector";

export const LikeEmailSettingsScreen = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [token] = useState(() => {
		const fragment = new URLSearchParams(location.hash.replace(/^#/, ""));
		return fragment.get("token")?.trim() || "";
	});
	const [frequency, setFrequency] = useState<LikeEmailFrequency>("daily");
	const [loading, setLoading] = useState(true);
	const [loaded, setLoaded] = useState(false);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const requestGeneration = useRef(0);

	useEffect(() => {
		const visibleParams = new URLSearchParams(location.search);
		const hadVisibleToken = visibleParams.has("token");
		visibleParams.delete("token");
		if (location.hash || hadVisibleToken) {
			const safeSearch = visibleParams.toString();
			navigate(`${location.pathname}${safeSearch ? `?${safeSearch}` : ""}`, { replace: true });
		}
	}, [location.hash, location.pathname, location.search, navigate]);

	useEffect(() => {
		const generation = ++requestGeneration.current;
		setLoading(true);
		setLoaded(false);
		setError("");
		if (!/^[A-Za-z0-9_-]{43}$/.test(token)) {
			setLoading(false);
			setError("This email settings link is not valid.");
			return;
		}

		incomingLikeEmailPreferenceApi
			.getPublic(token)
			.then((savedFrequency) => {
				if (generation !== requestGeneration.current) return;
				setFrequency(savedFrequency);
				setLoaded(true);
				setLoading(false);
			})
			.catch(() => {
				if (generation !== requestGeneration.current) return;
				setLoading(false);
				setError("We could not load these email settings. The link may have expired.");
			});

		return () => {
			requestGeneration.current += 1;
		};
	}, [token]);

	const savePreference = async () => {
		if (!/^[A-Za-z0-9_-]{43}$/.test(token) || loading || saving) return;
		const generation = ++requestGeneration.current;
		setSaving(true);
		setMessage("");
		setError("");
		try {
			const savedFrequency = await incomingLikeEmailPreferenceApi.savePublic(token, frequency);
			if (generation !== requestGeneration.current) return;
			setFrequency(savedFrequency);
			setMessage("Your Likes You email preference has been saved.");
		} catch {
			if (generation !== requestGeneration.current) return;
			setError("We could not save your preference. Please try again.");
		} finally {
			if (generation === requestGeneration.current) setSaving(false);
		}
	};

	return (
		<main className="min-h-svh bg-[#FFF7FD] px-4 py-10">
			<Column className="mx-auto max-w-2xl gap-5 rounded-2xl bg-white p-6 shadow-sm md:p-10">
				<img
					className="w-44 self-center"
					src="/hookup4u-Logo-BP.png"
					height={96}
					width={250}
					alt="Unjabbed"
				/>
				<TextComponent variant="h2" className="text-center font-bold text-primary">
					Likes You email settings
				</TextComponent>
				<TextComponent variant="h5" className="text-center">
					Choose how often Unjabbed sends a bundled email reminder about genuine new people who still like you.
				</TextComponent>
				<TextComponent variant="h6" className="text-center text-graySecondary">
					This only changes reminder emails. Immediate app alerts are separate and still follow your app notification settings.
				</TextComponent>

				{loading ? (
					<div className="flex justify-center py-8" aria-label="Loading email settings">
						<CircularProgress size={48} />
					</div>
				) : (
					<>
						<LikeEmailFrequencySelector
							value={frequency}
							disabled={saving || !loaded}
							onChange={(nextFrequency) => {
								setFrequency(nextFrequency);
								setMessage("");
								setError("");
							}}
						/>
						<CustomButton
							type="button"
							onClick={savePreference}
							loading={saving}
							disabled={saving || !loaded}
						>
							Save email preference
						</CustomButton>
					</>
				)}

				{message && (
					<TextComponent variant="h5" className="text-center font-semibold text-green-700" role="status">
						{message}
					</TextComponent>
				)}
				{error && (
					<TextComponent variant="h5" className="text-center font-semibold text-red-600" role="alert">
						{error}
					</TextComponent>
				)}
			</Column>
		</main>
	);
};
